import React, { useState } from 'react';
import { Button, Divider, Dropdown, Menu, Modal, Space } from 'antd';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { ColumnsType } from 'antd/lib/table';
import {
  TabContentWrapper,
  AppPageHeader,
  InfiniteTable,
  EditIcon,
  DeleteIcon,
  Group,
} from '@/components/_common';
import { ListCustomersQuery } from '@/core/domain/people/Customers/Customers.types';
import {
  useListSendOptions,
  useRemoveSendOption,
} from '@/core/domain/crm/SendOptions/SendOptions.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import {
  SendOption,
  SendOptionType,
} from '@/core/domain/crm/SendOptions/SendOptions.types';
import { getTableScrollX } from '@/utils/table';
import Authorities from '@/types/Authority';
import { PageProps } from '@/types/PageProps';
import { useDebounce } from '@/hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import IntegrationConceptBr from '@/components/CRM/IntegrationConceptBr';
import IntegrationPremaCar from '@/components/CRM/IntegrationPremaCar';
import IntegrationRotaAutotech from '@/components/CRM/IntegrationRotaAutotech';

const shippingOptions = [
  { key: SendOptionType.custom, label: 'Personalizado' },
  { key: SendOptionType.birthdays, label: 'Aniversariante' },
  { key: SendOptionType.nextReviews, label: 'Próxima revisão' },
  {
    key: SendOptionType.futureServices,
    label: 'Serviços futuros',
  },
  { key: SendOptionType.satisfactionSurvey, label: 'Pesquisa de satisfação' },
  { key: SendOptionType.emailMarketing, label: 'E-mail marketing' },
  {
    key: SendOptionType.clientWithoutMovement,
    label: 'Clientes sem movimento',
    disabled: true,
  },
  { key: SendOptionType.recoveryOfDebts, label: 'Cobrança' },
];

const ShippingOptionsPage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const [queryParams, setQueryParams] = useState<
    ListCustomersQuery | undefined
  >({
    order: 'ASC',
  });
  const history = useHistory();

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, hasNextPage, fetchNextPage, refetch } =
    useListSendOptions(queryDebounce);

  const sendOptions = flatInfiniteData(data);

  const removeSendOption = useRemoveSendOption();

  const columns: ColumnsType<SendOption> = [
    {
      key: 'title',
      dataIndex: 'title',
      title: intl.formatMessage({ id: 'shippingOptions.list.title' }),
      width: 300,
      ellipsis: true,
    },
    {
      key: 'subject',
      dataIndex: 'subject',
      title: intl.formatMessage({ id: 'shippingOptions.list.subject' }),
      width: 300,
      ellipsis: true,
    },
    {
      key: 'action',
      title: intl.formatMessage({ id: 'shippingOptions.list.action' }),
      render: (_, row) => (
        <>
          <Space>
            <EditIcon
              redirect={`/app/customers/crm/shippingOptions/${row.id}?type=${row.type}&step=0`}
              disabled={!hasPermissions([Authorities.CRM_SENDOPTION_UPDATE])}
            />

            <Divider type="vertical" />

            <DeleteIcon
              onClick={() => handleRemoveClick(row)}
              disabled={
                !hasPermissions([Authorities.CRM_SENDOPTION_DELETE]) ||
                row.systemDefaultType
              }
            />
          </Space>
        </>
      ),
      width: 80,
      align: 'right',
      fixed: 'right',
    },
  ];

  const handleRemoveClick = (option: SendOption) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'shippingOptions.remove' }),
      content: `${intl.formatMessage({
        id: 'shippingOptions.remove.alert',
      })} ${option.title}`,
      onOk: async () => {
        await removeSendOption.mutateAsync(option.id);

        await refetch();
      },
      okType: 'danger',
    });
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        permissions={route.permissions}
        title={route.name}
        extra={
          <Dropdown
            disabled={!hasPermissions([Authorities.CRM_SENDOPTION_CREATE])}
            overlay={
              <Menu
                items={shippingOptions}
                onClick={(info) =>
                  history.push(
                    `/app/customers/crm/shippingOptions/create/emailMontage?type=${info.key}`,
                  )
                }
              />
            }
          >
            <Button block type="primary">
              <FormattedMessage id="shippingOptions.list.newShipment" />
            </Button>
          </Dropdown>
        }
      >
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          headerActions={
            <Group gap={24}>
              <IntegrationPremaCar />
              <IntegrationConceptBr />
              <IntegrationRotaAutotech />
            </Group>
          }
        />

        <InfiniteTable
          columns={columns}
          dataSource={sendOptions}
          loading={isFetching}
          rowKey={(row) => row.id}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />
      </TabContentWrapper>
    </>
  );
};

export default ShippingOptionsPage;
